/**
 * Dashboard Metric Snapshot API (CR-4751).
 *
 * Read-only endpoints that serve cached KPI values captured nightly by the
 * backend Celery task. No live computation happens here.
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';

export type PeriodType = 'daily' | 'weekly' | 'monthly' | 'quarterly';

export type MetricType =
  | 'open_order_value'
  | 'total_spend'
  | 'monthly_spend'
  | 'arrears_value'
  | 'arrears_count'
  | 'arrears_opening_value'
  | 'arrears_new_value'
  | 'arrears_resolved_value'
  | 'arrears_closing_value'
  | 'otif_percentage'
  | 'otd_percentage'
  | 'on_time_count'
  | 'early_count'
  | 'late_count'
  | 'total_grns'
  | 'ppm'
  | 'ncr_count'
  | 'ncr_open_count'
  | 'active_supplier_count'
  | 'appeals_count';

export type MetricUnit = 'currency' | 'percentage' | 'count' | 'ppm';

export interface DashboardSnapshot {
  id: number;
  company: number | null;
  company_name: string | null;
  group: number | null;
  group_name: string | null;
  site: number | null;
  site_name: string | null;
  snapshot_date: string;
  period_type: PeriodType;
  period_start: string | null;
  period_end: string | null;
  metric_type: MetricType;
  metric_unit: MetricUnit;
  metric_value: string;
  fx_rate_to_base: string | null;
  fx_captured_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface TrendPoint {
  snapshot_date: string;
  metric_value: string;
  fx_rate_to_base: string | null;
}

export interface ComparePeriod {
  label: 'current' | 'previous';
  snapshot_date: string | null;
  metric_value: string | null;
  period_start: string | null;
  period_end: string | null;
}

export interface SnapshotListParams {
  metric_type?: MetricType;
  period_type?: PeriodType;
  snapshot_date_from?: string;
  snapshot_date_to?: string;
  company?: number;
  group?: number;
  site?: number;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface TrendsParams {
  metric_type: MetricType;
  period_type?: PeriodType;
  date_from?: string;
  date_to?: string;
  group?: number;
  site?: number;
}

export interface CompareParams {
  metric_type: MetricType;
  period_type?: PeriodType;
  current_date?: string;
}

export const dashboardSnapshotsAPI = {
  list: (
    params?: SnapshotListParams,
  ): Promise<AxiosResponse<PaginatedResponse<DashboardSnapshot>>> =>
    api.get('/purchasing/dashboard-snapshots/', { params }),

  trends: (
    params: TrendsParams,
  ): Promise<AxiosResponse<TrendPoint[]>> =>
    api.get('/purchasing/dashboard-snapshots/trends/', { params }),

  latest: (
    period_type: PeriodType = 'daily',
  ): Promise<AxiosResponse<DashboardSnapshot[]>> =>
    api.get('/purchasing/dashboard-snapshots/latest/', { params: { period_type } }),

  compare: (
    params: CompareParams,
  ): Promise<AxiosResponse<ComparePeriod[]>> =>
    api.get('/purchasing/dashboard-snapshots/compare/', { params }),
};
